'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import md5 from 'md5';
import {
  useLazyRegisterQuery,
  useLazyLookupQuery,
  useLazyVerifyPhoneQuery,
  useLazyVerifyEmailQuery,
  useGetPageQuery,
  useLazyValidateQuery,
} from '@/src/api/api';
import { useLoginHandler } from '@/src/redux/userSlice/loginHandlers';
import useParamsEditor from '@/src/utils/useParamsEditor';
import passwordValidator from '@/src/utils/passwordValidator';
import PasswordValidation from './PasswordValidation';
import CountrySelector from '@components/CountrySelector';
import ModalTitle from '@components/ModalTitle';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import ErrorMsg from '@components/ErrorMsg';
import { setContactInfo } from '@/src/redux/userSlice/userSlice';
import useATSName from '@/src/utils/useATSName';
import Modal from '@components/Modal';
import useLocaleSuffix from '@/src/utils/useLocaleSuffix';

const phonePrefixes = {
  NO: '+47',
  SE: '+46',
  DK: '+45',
};

export default function ExtendedRegistrationForm() {
  const t = useTranslations('RegistrationForm');
  const g = useTranslations('general');
  const editParam = useParamsEditor();
  const login = useLoginHandler();
  const getLocaleSuffix = useLocaleSuffix();
  const ats = useATSName();
  const ifEniro = !!process.env.NEXT_PUBLIC_ENIRO_LOOKUP;

  /* Uses states */
  const [phoneNotFound, setPhoneNotFound] = useState(false);
  const [registerErrorsHTML, setRegisterErrorsHTML] = useState(<></>);
  const [passwordErrors, setPasswordErrors] = useState(
    'length lower upper digit special'
  );
  const steps = {
    phoneLookup: 'phoneLookup',
    userData: 'userData',
    phoneVerification: 'phoneVerification',
    updatePhone: 'updatePhone',
    emailVerification: 'emailVerification',
    updateEmail: 'updateEmail',
  };
  const [step, setStep] = useState(
    ifEniro ? steps.phoneLookup : steps.userData
  );
  const [isPrivate, setIsPrivate] = useState(undefined as boolean | undefined);
  const [modal, setModal] = useState('' as '' | 'terms' | 'privacy');

  /* Use Forms */
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = useForm();

  useEffect(() => {
    const defaultCountry = String(
      process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv'
        ? 'se'
        : process.env.NEXT_PUBLIC_DEFAULT_LOCALE
    ).toUpperCase();
    setValue('private_customer.address.country', defaultCountry);
  }, [setValue]);

  const {
    register: registerPhoneLookup,
    handleSubmit: handlePhoneLookup,
    getValues: getLookupValue,
  } = useForm();
  const {
    register: registerPhoneUpdate,
    handleSubmit: handlePhoneUpdate,
    setValue: setPhoneValue,
    formState: phoneUpdateState,
  } = useForm();
  const {
    register: registerEmailUpdate,
    handleSubmit: handleEmailUpdate,
    setValue: setEmailValue,
    formState: emailUpdateState,
  } = useForm();

  /* Use queries */
  const [
    registerQuery,
    { isFetching: isRegistering, isError: isRegisteringError },
  ] = useLazyRegisterQuery();
  const [phoneLookup, { data: lookupData, isFetching: isFetchingPhone }] =
    useLazyLookupQuery();
  const [
    validateUserQuery,
    { data: userValidation, isError: isValidationError },
  ] = useLazyValidateQuery();
  const [verifyPhoneQuery, { data: SMSVerification, isFetching }] =
    useLazyVerifyPhoneQuery();
  const [
    verifyEmailQuery,
    { data: MailVerification, isFetching: IsEmailFetching },
  ] = useLazyVerifyEmailQuery();
  const { data, isFetching: isLoading } = useGetPageQuery(modal, {
    skip: !modal,
  });
  const page = data?.data;

  /* Custom functions */
  const registerSubmit = (formData: any) => {
    if (!isPrivate) {
      setValue('private_customer', null);
      formData.private_customer = null;
    }
    registerQuery(formData).then((res) => {
      setRegisterErrorsHTML(<></>);
      if (
        res.isSuccess &&
        res.data.hasOwnProperty('data') &&
        res.data.data.hasOwnProperty('access_token')
      ) {
        let loginData = res.data.data;
        login({
          userId: loginData.user_id,
          loggedIn: true,
          accessToken: loginData.access_token,
          firstName: loginData.first_name,
          lastName: loginData.last_name,
        });
        setContactInfo({
          email: getValues('email'),
          cellphone: getValues('cellphone'),
          country: getValues('private_customer.address.country'),
        });
        editParam({ modal: 'first-login' });
        return;
      }

      let errors: string[][] = [];
      const error = res.error as any;
      if (
        error.hasOwnProperty('data') &&
        error.data.hasOwnProperty('message')
      ) {
        errors = Object.values(JSON.parse(error?.data?.message));
      } else if ('error' in error) {
        errors = [[error.error]];
      } else {
        errors = [['Unknown error. Try again']];
      }
      let output = (
        <>
          {errors.map((e: any) => {
            return (
              <>
                {e[0]}
                <br />
              </>
            );
          })}
        </>
      );
      setRegisterErrorsHTML(output);
      setStep(steps.userData);
    });
  };

  const searchByPhone = async (data: any) => {
    setPhoneNotFound(false);
    const country = getLookupValue('country').split('|')[0];

    // Remove all non-digit characters, keep leading zeros for the local part
    let phoneInt = data.phone.replace(/\D/g, '');

    // For Swedish numbers, remove leading zero if present when adding country code
    let phoneForLookup = phoneInt;
    if (country === 'SE' && phoneInt.startsWith('0')) {
      phoneForLookup = phoneInt.substring(1);
    }

    phoneLookup({
      query: phoneForLookup,
      country: country,
    }).then((res) => {
      if (res.isSuccess) {
        if (res.data.data.persons.length) {
          const person = res.data.data.persons[0];
          let phonePrefix =
            country === 'NO'
              ? '+47'
              : country === 'SE'
                ? '+46'
                : country === 'DK'
                  ? '+45'
                  : '';

          // Format the phone number for storage (without leading zero for Swedish numbers)
          const formattedPhone =
            country === 'SE' && phoneInt.startsWith('0')
              ? phoneInt.substring(1)
              : phoneInt;

          setValue('cellphone', phonePrefix + formattedPhone);
          setValue('firstname', person.name.firstName);
          setValue('lastname', person.name.lastName);
          setValue(
            'private_customer.address.postal_area',
            person.addresses[0]?.postalArea
          );
          setValue(
            'private_customer.address.postal_code',
            person.addresses[0]?.postalCode
          );
          setValue(
            'private_customer.address.address',
            `${person.addresses[0]?.streetName} ${person.addresses[0]?.streetNumber}`
          );
          setValue(
            'private_customer.address.country',
            getLookupValue('country')
          );
          setStep(steps.userData);
          return;
        }
      }
      setPhoneNotFound(true);
    });
  };

  const registrationPasswordValidator = (password: string) => {
    let result = passwordValidator(password);
    if (!result) {
      result = '';
    }
    setPasswordErrors(result);
  };

  const goToPhoneVerification = () => {
    setValue('verification', '');
    validateUserQuery({
      cellphone: getValues('cellphone'),
      email: getValues('email'),
    }).then((res: any) => {
      if (!res.isSuccess) {
        setStep(steps.userData);
        let errors = res?.error?.data?.errors;
        let errorMsg = <></>;
        if (errors.cellphone) {
          errorMsg = (
            <>
              {errorMsg}
              {t('validation.cellphoneDuplicate')}
              <br />
            </>
          );
        }
        if (errors.email) {
          errorMsg = (
            <>
              {errorMsg}
              {t('validation.mailDuplicate')}
              <br />
            </>
          );
        }
        setRegisterErrorsHTML(<>{errorMsg}</>);
      } else {
        verifyPhoneQuery({ number: getValues('cellphone') }).then((res) => {});
        setStep(steps.phoneVerification);
      }
    });
  };

  const goToEmailVerification = () => {
    setValue('verificationmail', '');
    validateUserQuery({
      cellphone: getValues('cellphone'),
      email: getValues('email'),
    }).then((res: any) => {
      if (!res.isSuccess) {
        setStep(steps.userData);
        let errors = res?.error?.data?.errors;
        let errorMsg = <></>;
        if (errors.cellphone) {
          errorMsg = (
            <>
              {errorMsg}
              {t('validation.cellphoneDuplicate')}
              <br />
            </>
          );
        }
        if (errors.email) {
          errorMsg = (
            <>
              {errorMsg}
              {t('validation.mailDuplicate')}
              <br />
            </>
          );
        }
        setRegisterErrorsHTML(<>{errorMsg}</>);
      } else {
        verifyEmailQuery({ email: getValues('email') }).then((res) => {});
        setStep(steps.emailVerification);
      }
    });
  };

  const validateForm = () => {
    trigger().then((passed) => {
      if (passed) {
        const phoneNumber = getValues('cellphone');
        const countryCode = phoneNumber.slice(0, 3);
        const norwayCode = '+47';
        const swedenCode = '+46';
        const polandCode = '+48';
        setPhoneValue('phone', getValues('cellphone'));
        setEmailValue('email', getValues('email'));
        if (
          countryCode === norwayCode ||
          countryCode === swedenCode ||
          countryCode === polandCode
        )
          goToPhoneVerification();
        else {
          goToEmailVerification();
        }
      }
    });
  };

  const phoneUpdate = (formData: any) => {
    // Get the current country code and check if it's Sweden
    const country = getLookupValue('country');
    const countryCode =
      phonePrefixes[country as keyof typeof phonePrefixes] || '';

    // Remove country code and leading plus, then remove any non-digits
    let localNumber = formData.phone
      .replace(new RegExp(`^\\+?${countryCode}`), '')
      .replace(/\D/g, '');

    // For Swedish numbers, remove leading zero if present when adding country code
    if (country === 'SE' && localNumber.startsWith('0')) {
      localNumber = localNumber.substring(1);
    }

    // Rebuild the full number with proper formatting
    const fullNumber = `${countryCode}${localNumber}`;
    setValue('cellphone', fullNumber);
    goToPhoneVerification();
  };

  const emailUpdate = (formData: any) => {
    setValue('email', formData.email);
    goToEmailVerification();
  };

  /* HTML Components */
  const intro = t.rich('lookup.instructions', {
    ats,
    br: () => <br />,
    list: (chunks) => <ul>{chunks}</ul>,
    item: (chunks) => <li className='list-disc'>{chunks}</li>,
    strong: (chunks) => <b>{chunks}</b>,
    noBonus: (chunks) => {
      if (process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'no') {
        return chunks;
      }
      return null;
    },
    seBonus: (chunks) => {
      if (process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv') {
        return chunks;
      }
      return null;
    },
  });

  const getPhoneFormat = (country: string) => {
    let placeHolder = '';
    switch (country) {
      case 'SE':
        placeHolder = '070-123 45 67';
        break;
      case 'NO':
        placeHolder = '406 12 345';
        break;
      case 'DK':
        placeHolder = '32 12 34 56';
        break;
      default:
        placeHolder = '';
        break;
    }
    return placeHolder;
  };
  const [phoneInputPlaceholder, setPhoneInputPlaceholder] = useState(
    getPhoneFormat(
      process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv'
        ? 'SE'
        : String(process.env.NEXT_PUBLIC_DEFAULT_LOCALE).toUpperCase()
    )
  );
  const countryChanged = (e: any) => {
    setPhoneInputPlaceholder(getPhoneFormat(String(e)));
  };

  useEffect(() => {
    setValue(
      'cellphone',
      phonePrefixes[getLookupValue('country') as keyof typeof phonePrefixes]
    );
  }, [setValue, getLookupValue]);

  return (
    <div className='w-full'>
      {
        // Step 1: phone lookup
      }
      {step === steps.phoneLookup && (
        <>
          <ModalTitle title={t('title')} />
          <div className='flex flex-col gap-16 md:max-w-[800px] md:flex-row'>
            <div className='md:w-1/2'>{intro}</div>
            <div className='flex flex-col justify-center md:w-1/2'>
              <form
                method='post'
                onSubmit={handlePhoneLookup(searchByPhone)}
                className='rounded-sm border border-white bg-gray-800 p-6 pb-0 md:mb-4'
              >
                <div className='-mx-3 flex flex-wrap'>
                  <div className='mb-4 flex w-full flex-wrap px-3 md:flex-nowrap '>
                    <select
                      className='mb-4 box-border block w-full rounded-sm bg-white px-2 py-3 text-dark md:mb-0 md:w-auto md:rounded-l-sm md:rounded-r-none'
                      {...registerPhoneLookup('country', {
                        value: String(
                          process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv'
                            ? 'se'
                            : process.env.NEXT_PUBLIC_DEFAULT_LOCALE
                        ).toUpperCase(),
                        onChange: (e) => {
                          setValue(
                            'cellphone',
                            phonePrefixes[
                              e.currentTarget
                                .value as keyof typeof phonePrefixes
                            ] + getLookupValue('phone')
                          );
                          setPhoneInputPlaceholder(
                            getPhoneFormat(e.currentTarget.value)
                          );
                        },
                      })}
                      defaultValue={String(
                        process.env.NEXT_PUBLIC_DEFAULT_LOCALE
                      ).toUpperCase()}
                    >
                      <option value='NO'>(NO) +47</option>
                      <option value='SE'>(SE) +46</option>
                      <option value='DK'>(DK) +45</option>
                    </select>
                    <input
                      id='phone'
                      className='box-border block w-full appearance-none rounded-sm border p-2 text-dark md:rounded-l-none md:rounded-r-sm'
                      placeholder={phoneInputPlaceholder}
                      {...registerPhoneLookup('phone', {
                        required: {
                          value: true,
                          message: t('lookup.validationPhoneRequired'),
                        },
                        onChange: (e) => {
                          setValue(
                            'cellphone',
                            phonePrefixes[
                              getLookupValue(
                                'country'
                              ) as keyof typeof phonePrefixes
                            ] + e.currentTarget.value
                          );
                        },
                      })}
                    />
                  </div>
                  <div className='mb-6 w-full px-3 md:mb-0'>
                    <input
                      type='submit'
                      value={t('lookup.submitButtonText')}
                      className='w-full cursor-pointer appearance-none rounded-sm border p-2 font-bold uppercase transition-all hover:border-brand hover:bg-brand hover:text-dark md:rounded-l-none'
                    />
                  </div>
                  <div className='w-full px-3 py-4'>
                    {isFetchingPhone && t('lookup.searchingStatusText')}
                    {phoneNotFound && (
                      <span className='text-brand-alt '>
                        {t('lookup.noResultsText')}
                      </span>
                    )}
                    {!!errors.phone && (
                      <span className='text-brand-alt'>
                        {String(errors.phone.message)}
                      </span>
                    )}
                    <div className='mb-4 w-full text-center'>{g('or')}</div>
                    <button
                      type='button'
                      onClick={() => setStep(steps.userData)}
                      className='w-full cursor-pointer appearance-none rounded-sm border p-2 font-bold uppercase transition-all hover:border-brand hover:bg-brand hover:text-dark md:rounded-l-none'
                    >
                      {t('lookup.skip')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {
        // Step 2: user data
      }
      {step === steps.userData && (
        <>
          <div className={`${modal ? 'hidden' : ''}`}>
            <ModalTitle title={t('title')} />
            <div className='-mx-3 flex flex-wrap'>
              {!ifEniro && <div className='mb-6 w-full px-3'>{intro}</div>}
              <div className='mb-6 w-full px-3 font-semibold text-red-500'>
                {(isRegisteringError || isValidationError) &&
                  registerErrorsHTML}
              </div>
            </div>
            <form method='post' onSubmit={handleSubmit(registerSubmit)}>
              <div className='-mx-3 flex flex-wrap items-start'>
                <div className='mb-6 w-full px-3 md:w-1/2'>
                  <label
                    htmlFor='cellphone'
                    className='mb-2 block text-sm font-bold uppercase'
                  >
                    {t('label.cellphone')}
                  </label>
                  <div className='rounded-sm bg-white py-2 pl-2 text-black'>
                    <input
                      type='hidden'
                      {...register('cellphone', {
                        required: {
                          value: true,
                          message: t('validation.phoneRequired'),
                        },
                      })}
                    />
                    <PhoneInput
                      placeholder={phoneInputPlaceholder}
                      onCountryChange={(e) => {
                        countryChanged(e);
                        // When country changes, update the phone number format
                        const currentNumber =
                          getValues('cellphone')?.replace(/\D/g, '') || '';
                        const countryCode =
                          phonePrefixes[e as keyof typeof phonePrefixes] || '';
                        // Remove the old country code if it exists
                        const localNumber = currentNumber.replace(
                          new RegExp(
                            `^(${Object.values(phonePrefixes)
                              .map((prefix) => prefix.replace(/\+/g, '\\+'))
                              .join('|')})`
                          ),
                          ''
                        );
                        setValue('cellphone', countryCode + localNumber);
                      }}
                      defaultCountry={getLookupValue('country')}
                      onChange={(e) => {
                        // Keep the full number as is, the library handles the formatting
                        setValue('cellphone', e || '');
                      }}
                    />
                  </div>
                  <ErrorMsg errors={errors} name='cellphone' />
                </div>
                <div className='w-full'></div>
                <div className='mb-6 w-full px-3 md:w-1/2'>
                  {ifEniro && (
                    <button
                      onClick={() => setStep(steps.phoneLookup)}
                      className='mb-6 flex w-full items-center justify-center rounded-sm border p-2 text-center'
                    >
                      <MdChevronLeft /> {t('returnToLookup')}
                    </button>
                  )}
                </div>
              </div>
              <div className='-mx-3 mb-6 flex flex-wrap'>
                <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                  <label
                    htmlFor='email'
                    className='mb-2 block text-sm font-bold uppercase'
                  >
                    {t('label.email')}
                  </label>
                  <input
                    type='email'
                    id='email'
                    className='block w-full appearance-none rounded-sm p-2 text-dark'
                    placeholder={t('placeholder.email')}
                    {...register('email', {
                      required: {
                        value: true,
                        message: t('validation.emailRequired'),
                      },
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: t('validation.emailFormat'),
                      },
                    })}
                  />
                  <ErrorMsg errors={errors} name='email' />
                </div>
                <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                  <label
                    htmlFor='email'
                    className='mb-2 block text-sm font-bold uppercase'
                  >
                    {t('label.emailConfirmation')}
                  </label>
                  <input
                    type='email'
                    id='confirmation_email'
                    className='block w-full appearance-none rounded-sm p-2 text-dark'
                    placeholder={t('placeholder.email')}
                    {...register('emailConfirmation', {
                      validate: (value, formValues) => {
                        if (value !== formValues.email) {
                          return t('validation.emailNotMatching');
                        }
                      },
                    })}
                  />
                  <>
                    <ErrorMsg errors={errors} name='emailConfirmation' />
                  </>
                </div>
              </div>
              <div className='-mx-3 mb-2 flex flex-wrap'>
                <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                  <label
                    htmlFor='firstname'
                    className='mb-2 block text-sm font-bold uppercase'
                  >
                    {t('label.firstName')}
                  </label>
                  <input
                    id='firstname'
                    className='block w-full appearance-none rounded-sm p-2 text-dark'
                    placeholder={t('placeholder.firstName')}
                    {...register('firstname', {
                      required: {
                        value: true,
                        message: t('validation.firstNameRequired'),
                      },
                    })}
                  />
                  <ErrorMsg errors={errors} name='firstname' />
                </div>
                <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                  <label
                    htmlFor='lastname'
                    className='mb-2 block text-sm font-bold uppercase'
                  >
                    {t('label.lastName')}
                  </label>
                  <input
                    id='lastname'
                    className='block w-full appearance-none rounded-sm p-2 text-dark'
                    placeholder={t('placeholder.lastName')}
                    {...register('lastname', {
                      required: {
                        value: true,
                        message: t('validation.lastNameRequired'),
                      },
                    })}
                  />
                  <ErrorMsg errors={errors} name='lastname' />
                </div>
              </div>
              <p className='mb-2 mt-12 block text-sm font-bold uppercase'>
                {t('label.as')}
              </p>
              <div className='-mx-3 mb-12 flex flex-wrap'>
                <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                  <button
                    type='button'
                    className={`w-full rounded-sm border p-2 font-bold uppercase hover:border-brand hover:bg-brand hover:text-dark ${
                      isPrivate ? 'border-brand bg-brand text-dark' : ''
                    }`}
                    onClick={() => setIsPrivate(true)}
                  >
                    {t('label.private')}
                  </button>
                </div>
                <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                  <button
                    type='button'
                    className={`w-full rounded-sm border p-2 font-bold uppercase hover:border-brand hover:bg-brand hover:text-dark ${
                      isPrivate === false
                        ? 'border-brand bg-brand text-dark'
                        : ''
                    }`}
                    onClick={() => setIsPrivate(false)}
                  >
                    {t('label.company')}
                  </button>
                </div>
              </div>
              <div className={`${isPrivate ? 'block' : 'hidden'}`}>
                <div className='-mx-3 mb-6 flex flex-wrap'>
                  <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                    <label className='mb-2 block text-sm font-bold uppercase'>
                      {t('label.address')}
                    </label>
                    <input
                      id='address'
                      className='block w-full appearance-none rounded-sm p-2 text-dark'
                      placeholder={t('placeholder.address')}
                      {...register('private_customer.address.address', {
                        validate: {
                          required: (value) => {
                            if (!isPrivate) return undefined;
                            if (!value) return t('validation.address');
                          },
                        },
                      })}
                    />
                    <ErrorMsg
                      errors={errors}
                      name='private_customer.address.address'
                    />
                  </div>
                  <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                    <label className='mb-2 block text-sm font-bold uppercase'>
                      {t('label.postalcode')}
                    </label>
                    <input
                      id='postalcode'
                      type='number'
                      className='block w-full appearance-none rounded-sm p-2 text-dark'
                      placeholder={t('placeholder.postalcode')}
                      {...register('private_customer.address.postal_code', {
                        validate: {
                          required: (value) => {
                            if (!isPrivate) return undefined;
                            if (!value) return t('validation.postalcode');
                          },
                        },
                      })}
                    />
                    <ErrorMsg
                      errors={errors}
                      name='private_customer.address.postal_code'
                    />
                  </div>
                </div>
                <div className='-mx-3 mb-6 flex flex-wrap'>
                  <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                    <label className='mb-2 block text-sm font-bold uppercase'>
                      {t('label.city')}
                    </label>
                    <input
                      id='city'
                      className='block w-full appearance-none rounded-sm p-2 text-dark'
                      placeholder={t('placeholder.city')}
                      {...register('private_customer.address.postal_area', {
                        validate: {
                          required: (value) => {
                            if (!isPrivate) return undefined;
                            if (!value) return t('validation.city');
                          },
                        },
                      })}
                    />
                    <ErrorMsg
                      errors={errors}
                      name='private_customer.address.postal_area'
                    />
                  </div>
                  <div className='mb-6 w-full px-3 md:mb-0 md:w-1/2'>
                    <label
                      className='mb-2 block text-sm font-bold uppercase'
                      id='country-label'
                    >
                      {t('label.country')}
                    </label>
                    <CountrySelector
                      onChange={(option: any) => {
                        setValue(
                          'private_customer.address.country',
                          option.value
                        );
                        trigger('private_customer.address.country');
                      }}
                      country={
                        getValues('private_customer.address.country') ||
                        String(
                          process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv'
                            ? 'se'
                            : process.env.NEXT_PUBLIC_DEFAULT_LOCALE
                        ).toUpperCase()
                      }
                    />
                    <input
                      type='hidden'
                      {...register('private_customer.address.country', {
                        validate: {
                          required: (value) => {
                            if (!isPrivate) return undefined;
                            if (!value) return t('validation.country');
                            return undefined;
                          },
                        },
                      })}
                    />
                    <ErrorMsg
                      errors={errors}
                      name='private_customer.address.country'
                    />
                  </div>
                </div>
              </div>
              <div
                className={`mt-2 ${isPrivate === false ? 'block' : 'hidden'}`}
              >
                {t('privateCompanyMessage')}
              </div>
              {typeof isPrivate !== 'undefined' && (
                <>
                  <div className='-mx-3 my-6 flex flex-wrap'>
                    <div className='w-full px-3 md:w-1/2'>
                      <label
                        htmlFor='password'
                        className='mb-2 block text-sm font-bold uppercase'
                      >
                        {t('label.password')}
                      </label>
                      <input
                        type='password'
                        id='password'
                        className='block w-full appearance-none rounded-sm p-2 text-dark'
                        placeholder={t('placeholder.password')}
                        {...register('password', {
                          required: {
                            value: true,
                            message: t('validation.passwordRequired'),
                          },
                          onChange: (e) => {
                            registrationPasswordValidator(
                              e.currentTarget.value
                            );
                          },
                          validate: passwordValidator,
                        })}
                      />
                    </div>
                    <div className='w-full px-3 md:w-1/2'>
                      <label
                        htmlFor='password_confirmation'
                        className='mb-2 block text-sm font-bold uppercase'
                      >
                        {t('label.confirmPassword')}
                      </label>
                      <input
                        type='password'
                        id='password_confirmation'
                        className='block w-full appearance-none rounded-sm p-2 text-dark'
                        placeholder={t('placeholder.password')}
                        {...register('password_confirmation', {
                          required: {
                            value: true,
                            message: t('validation.passwordNeedsConirmation'),
                          },
                          validate: (value, formValues) => {
                            if (value !== formValues.password) {
                              return t('validation.passwordNotMatching');
                            }
                          },
                        })}
                      />
                    </div>
                    <div className='my-4 px-3'>
                      <ErrorMsg errors={errors} name='password_confirmation' />
                      <div className='mt-2 w-full'>
                        <PasswordValidation
                          passwordValidation={passwordErrors}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='-mx-3 mb-3'>
                    <div className='flex items-center gap-2 px-3'>
                      <input
                        type='checkbox'
                        id='conditions'
                        value={1}
                        {...register('conditions', {
                          required: {
                            value: true,
                            message: t('validation.conditionsRequired'),
                          },
                        })}
                      />
                      <label htmlFor='conditions' className='block text-sm'>
                        {t.rich('label.terms', {
                          a: (chunks) => (
                            <button
                              type='button'
                              onClick={() => setModal('terms')}
                              className='underline'
                            >
                              {chunks}
                            </button>
                          ),
                        })}
                      </label>
                    </div>
                    <div className='mb-3 flex items-center gap-2 px-3'>
                      <ErrorMsg errors={errors} name='conditions' />
                    </div>
                  </div>
                  <div className='-mx-3'>
                    <div className='flex items-center gap-2 px-3'>
                      <input
                        type='checkbox'
                        id='terms'
                        value={1}
                        {...register('terms', {
                          required: {
                            value: true,
                            message: t('validation.termsRequired'),
                          },
                        })}
                      />
                      <label htmlFor='terms' className='block text-sm'>
                        {t.rich('label.privacy', {
                          ats,
                          a: (chunks) => (
                            <button
                              type='button'
                              onClick={() => setModal('privacy')}
                              className='underline'
                            >
                              {chunks}
                            </button>
                          ),
                        })}
                      </label>
                    </div>
                    <div className='mb-3 flex items-center gap-2 px-3'>
                      <ErrorMsg errors={errors} name='terms' />
                    </div>
                  </div>
                  {/*<div className='-mx-3 mb-3'>
                  <div className='flex items-center gap-2 px-3'>
                    <input
                      type='checkbox'
                      id='newsletter'
                      value={1}
                      {...register('newsletter')}
                    />
                    <label htmlFor='newsletter' className='block text-sm'>
                      {t('label.newsletter', { ats })}
                    </label>
                  </div>
                  <div className='mb-3 flex items-center gap-2 px-3'>
                    <ErrorMsg errors={errors} name='newsletter' />
                  </div>
                </div>*/}
                  <div className='-mx-3 mt-8 flex flex-wrap'>
                    <div className='w-full px-3'>
                      <button
                        type='button'
                        onClick={validateForm}
                        className='w-full cursor-pointer appearance-none rounded-sm border border-brand bg-brand p-2 font-bold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
                      >
                        {t('label.submitButtonText')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
          {modal && (
            <Modal customClose={() => setModal('')}>
              {isLoading && <>{g('loading')}...</>}
              {!isLoading && page && (
                <>
                  <h2 className='text-lg font-bold uppercase text-brand'>
                    {page[getLocaleSuffix('title')]}
                  </h2>
                  <div
                    className='single-page-content prose mt-2'
                    dangerouslySetInnerHTML={{
                      __html: page[getLocaleSuffix('body')],
                    }}
                  />
                </>
              )}
            </Modal>
          )}
        </>
      )}

      {
        // Step 3: phone verification
      }
      <div
        className={`${
          step === steps.phoneVerification ? 'block' : 'hidden'
        } flex flex-col text-center`}
      >
        {isRegistering ? (
          t('sendingRegistration')
        ) : (
          <>
            <ModalTitle title={t('titlePhoneVerification')} />
            <div className='mb-6 flex flex-col'>
              {t('codeSendTo')}:
              <span className='text-2xl'>{getValues('cellphone')}</span>
            </div>
            <label className='text-semibold flex flex-col'>
              {t('enterCode')}
              {step === steps.phoneVerification && (
                <input
                  className='mt-2 rounded-sm px-4 py-2 text-center text-dark'
                  placeholder='00 00 00'
                  {...register('verification', {
                    validate: (value) => {
                      console.log('Alvin er kul');
                      if (step === steps.userData) {
                        return undefined;
                      }
                      console.log(step);
                      if (md5(value) !== SMSVerification?.data?.code) {
                        console.log('Verdi:', value);
                        console.log('Noe:', SMSVerification);
                        return t('validation.verificationCodeIncorrect');
                      }
                    },
                  })}
                />
              )}
            </label>
            <button
              type='submit'
              className='mt-4 w-full rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
              onClick={handleSubmit(registerSubmit)}
            >
              {t('verify')}
            </button>
            <div className='mx-auto mt-4 font-semibold text-red-500'>
              <ErrorMsg errors={errors} name='verification' />
            </div>
            <div className='mt-8 flex flex-col justify-center gap-1'>
              <p className='text-center'>{t('codeNotReceived')}</p>
              <button
                className='rounded-sm uppercase text-brand underline'
                onClick={() => setStep(steps.updatePhone)}
              >
                {t('checkPhone')}
              </button>
            </div>
            {/* <button
              onClick={() => setStep(steps.userData)}
              className='mt-8 flex items-center'
            >
              <MdChevronLeft /> {t('goBack')}
            </button> */}
          </>
        )}
      </div>
      {step === steps.updatePhone && (
        <div className='grid-col-1 grid'>
          <ModalTitle title={t('titleUpdatePhone')} />
          <form method='post' onSubmit={handlePhoneUpdate(phoneUpdate)}>
            <label className='flex flex-col gap-1'>
              <p className='mb-2'>{t('checkOrUpdatePhone')}</p>
              <input
                className='rounded-sm px-4 py-2 text-center text-dark'
                {...registerPhoneUpdate('phone', {
                  required: {
                    value: true,
                    message: t('validation.phoneRequired'),
                  },
                })}
              />
            </label>
            <ErrorMsg errors={phoneUpdateState.errors} name='phone' />
            <button
              type='submit'
              className='mt-4 w-full rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
            >
              {t('resendCode')}
            </button>
          </form>
          <button
            onClick={() => setStep(steps.phoneVerification)}
            className='mt-8 flex items-center'
          >
            <MdChevronLeft /> {t('goBack')}
          </button>
        </div>
      )}

      {
        // Step 4: email verification
      }
      <div
        className={`${
          step === steps.emailVerification ? 'block' : 'hidden'
        } flex flex-col text-center`}
      >
        {isRegistering ? (
          t('sendingRegistration')
        ) : (
          <>
            <ModalTitle title={t('titleEmailVerification')} />
            <div className='mb-6 flex flex-col'>
              {t('codeSendTo')}:
              <span className='text-2xl'>{getValues('email')}</span>
            </div>
            <label className='text-semibold flex flex-col'>
              {t('enterCode')}
              {step === steps.emailVerification && (
                <input
                  className='mt-2 rounded-sm px-4 py-2 text-center text-dark'
                  placeholder='123456'
                  {...register('verificationmail', {
                    validate: (value) => {
                      if (step === steps.userData) {
                        return undefined;
                      }
                      console.log(step);
                      if (md5(value) !== MailVerification?.data?.code) {
                        return t('validation.verificationCodeIncorrect');
                      }
                    },
                  })}
                />
              )}
            </label>
            <button
              type='submit'
              className='mt-4 w-full rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
              onClick={handleSubmit(registerSubmit)}
            >
              {t('verify')}
            </button>
            <div className='mx-auto mt-4 font-semibold text-red-500'>
              <ErrorMsg errors={errors} name='verification' />
            </div>
            <div className='mt-8 flex flex-col justify-center gap-1'>
              <p className='text-center'>{t('codeNotReceived')}</p>
              <button
                className='rounded-sm uppercase text-brand underline'
                onClick={() => setStep(steps.updateEmail)}
              >
                {t('checkEmail')}
              </button>
            </div>
            {/* <button
        onClick={() => setStep(steps.userData)}
        className='mt-8 flex items-center'
      >
        <MdChevronLeft /> {t('goBack')}
      </button> */}
          </>
        )}
      </div>
      {step === steps.updateEmail && (
        <div className='grid-col-1 grid'>
          <ModalTitle title={t('titleUpdateEmail')} />
          <form method='post' onSubmit={handleEmailUpdate(emailUpdate)}>
            <label className='flex flex-col gap-1'>
              <p className='mb-2'>{t('checkOrUpdateEmail')}</p>
              <input
                className='rounded-sm px-4 py-2 text-center text-dark'
                {...registerEmailUpdate('email', {
                  required: {
                    value: true,
                    message: t('validation.emailRequired'),
                  },
                })}
              />
            </label>
            <ErrorMsg errors={emailUpdateState.errors} name='email' />
            <button
              type='submit'
              className='mt-4 w-full rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
            >
              {t('resendCode')}
            </button>
          </form>
          <button
            onClick={() => setStep(steps.emailVerification)}
            className='mt-8 flex items-center'
          >
            <MdChevronLeft /> {t('goBack')}
          </button>
        </div>
      )}
    </div>
  );
}
